// Parent component (where useForm is called)
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useForm,
  FormProvider,
  Controller,
  useWatch,
  SubmitHandler,
  UseFormGetValues,
  UseFormSetValue,
  FieldErrors,
  FieldPath,
} from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Collapse,
  Alert,
  AlertTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { resetAllErrorInfo } from '../../../services/DataHelperFunctions';
import {
  EAction,
  EActionType,
  ECommonDialogType,
  RecordLimit,
  RegExPatterns,
} from '../../../data/Constants';
import { AppStore, AppDispatch, AppState } from '../../../store';
import { getApplicationDetails } from '../../../store/states/ApplicationSlice';
import { getFeatureFlags } from '../../../store/states/FeatureFlagSlice';
import { createApiKey, getApiKeys, updateApiKey } from '../../../store/states/ApiKeySlice';
import { ErrorMessage } from '../../../components/ErrorMessage';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { resetDialogMessage, setDialogMessage } from '../../../store/states/DialogSlice';
import { TFunction } from 'i18next';

import type {
  IApplication,
  IApiKey,
  IPrivilegeAccessAssociation,
  IManipulationProps,
  IError,
  IAPIResponse,
  IApiKeyProperty,
  ICheckboxGroupChangeField,
  IApikeyFeatureFlag,
  ICheckboxChangeField,
  IAccessType,
  ISupportDetail,
  IApplicationFeatureFlags,
  IStructuredFlagsObj,
  IAPIKeyDetailsProp,
  ISupportDetailsProp,
  IManipulationFormBase,
  IRenderPrivilageAsscessAssociationsProp,
  IFeatureFlagsProp,
  IStructuredFeatureFlag,
} from '../../../../types';
const stringValidations = {
  name: { min: 3 },
};

//To call create and update apis
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IApiKey,
  t: TFunction,
  dispatch: AppDispatch
): void {
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateApiKey({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
          userEmail: formData.userEmail === '' ? null : formData.userEmail,
          supportDetails: formData.supportDetails,
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        'edit',
        dispatch
      );
    });
  } else {
    dispatch(
      createApiKey({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
          supportDetails: formData.supportDetails,
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        'create',
        dispatch
      );
    });
  }
}
function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  type: string,
  dispatch: AppDispatch
): void {
  const apikeys = AppStore.getState().administration.apikeys;
  if (!res || res.error) {
    const message = res?.error.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  } else if (type === 'edit') {
    const msg = `${successMessage}`;
    dispatch(setAlertMessage({ show: true, message: msg, type: 'success' }));
    dispatch(
      getApiKeys({
        page: apikeys.currentPage,
        limit: apikeys.recordsPerPage,
        searchCode: apikeys.searchKey,
      })
    );
    dispatch(resetDialogMessage());
  } else if (type === 'create') {
    dispatch(
      setDialogMessage({
        show: true,
        message: res.data,
        type: ECommonDialogType.ApiKeyCreateSuccess,
      })
    );
    dispatch(
      getApiKeys({
        page: apikeys.currentPage,
        limit: apikeys.recordsPerPage,
        searchCode: apikeys.searchKey,
      })
    );
  }
}

//This function set the applications to the state after fetching from the API
const setApplicationDetails = async (
  setApplications: (value: React.SetStateAction<IApplication[]>) => void,
  dispatch: AppDispatch
): Promise<void> => {
  const res = await dispatch(getApplicationDetails({ request: [], isActive: true }));

  const applications: IApplication[] =
    res.payload && typeof res.payload === 'object' && 'data' in res.payload
      ? (res.payload.data as IApplication[])
      : [];
  setApplications(applications);
};

const setFeatureFlagDetails = async (
  setFeatureFlags: (value: React.SetStateAction<IApplicationFeatureFlags[]>) => void,
  dispatch: AppDispatch
): Promise<void> => {
  //get all feature flags which are active
  const res = await dispatch(
    getFeatureFlags({ page: 1, limit: RecordLimit.default, searchCode: '', activeOnly: true })
  ).unwrap();
  const flags = res && typeof res === 'object' && 'data' in res ? res.data : [];
  const structuredFlagsObj: IStructuredFlagsObj = {};
  flags?.forEach((flag: IStructuredFeatureFlag & { applicationName: string }) => {
    if (!structuredFlagsObj[flag.applicationName]) {
      structuredFlagsObj[flag.applicationName] = [];
    }
    structuredFlagsObj[flag.applicationName] = [
      ...structuredFlagsObj[flag.applicationName],
      {
        page: flag.page,
        flagName: flag.flagName,
        description: flag.description,
        isActive: flag.isActive,
        isVisible: flag.isVisible,
        isEditable: flag.isEditable,
      },
    ];
  });

  //Put the Applications and it's Feature flags in an array
  const structuredFlagsArr: IApplicationFeatureFlags[] = [];
  Object.keys(structuredFlagsObj).forEach((key) => {
    const applicationObj: IApplicationFeatureFlags = {
      applicationName: key,
      featureFlags: structuredFlagsObj[key],
    };
    structuredFlagsArr.push(applicationObj);
  });
  //set the feature flag details
  setFeatureFlags(structuredFlagsArr);
};

const handlePropertyChangeCheck = (
  value: string,
  checked: boolean,
  origin: string,
  getValues: UseFormGetValues<IApiKey>,
  setValue: UseFormSetValue<IApiKey>,
  properties: { [key: string]: IApiKeyProperty[] },
  field?: ICheckboxGroupChangeField
) => {
  const currentProperties = JSON.parse(JSON.stringify(getValues('properties')));
  if (value === 'ALL') {
    if (checked) {
      setValue(`properties.${origin}` as FieldPath<IApiKey>, ['ALL'], {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue(`properties.${origin}` as FieldPath<IApiKey>, [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  } else {
    const value1 = field?.value ?? [];

    if (checked) {
      if (currentProperties[origin].length === properties[origin].length - 2) {
        setValue(`properties.${origin}` as FieldPath<IApiKey>, ['ALL'], {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        field?.onChange([...value1, value]);
      }
    } else {
      field?.onChange(value1.filter((selected) => selected !== value));
    }
  }
};

const loadFormData = async (
  props: IManipulationProps,
  reset: (values?: IApiKey) => void,
  setInitialApiKeyData: (value: React.SetStateAction<IApiKey>) => void,
  dispatch: AppDispatch,
  setSelectedFeatureFlags: (value: React.SetStateAction<IApikeyFeatureFlag[]>) => void
) => {
  const apikeyDetails = await dispatch(
    getApiKeys({
      page: 0,
      limit: 0,
      searchCode: props.id,
      skipUpdate: true,
    })
  ).unwrap();

  if (!('data' in apikeyDetails) || !apikeyDetails.data) {
    return;
  }
  const apiKeyData = apikeyDetails.data[0] as IApiKey;
  if (apiKeyData.properties?.['configit'] == null) {
    apiKeyData.properties = { configit: [], pdm: [] };
  }

  const InitfeatureFlags = structuredClone(apiKeyData.featureFlags) as IApikeyFeatureFlag[];
  const updatedFlag: IApikeyFeatureFlag[] = [];
  const featureFlagsForform: Record<string, Omit<IApikeyFeatureFlag, 'name'>> = {};
  InitfeatureFlags.forEach((f: IApikeyFeatureFlag) => {
    updatedFlag.push(JSON.parse(JSON.stringify(f)));
    const { name, ...rest } = f;
    featureFlagsForform[name] = rest;
  });
  const initData: IApiKey = {
    ...apiKeyData,
    supportDetails: apiKeyData.supportDetails
      ? apiKeyData.supportDetails
      : { message: '', url: null },
    privileges: apiKeyData.privileges ?? [],
    properties: apiKeyData.properties,
    featureFlags: featureFlagsForform as object as IApikeyFeatureFlag[],
  };
  setSelectedFeatureFlags(updatedFlag);
  reset({ ...initData });
  setInitialApiKeyData(initData);
};

const addAlertMessage = (
  error: IError,
  setDisplayAlertMessage: (value: React.SetStateAction<boolean>) => void
): void => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};

export const ApiKeyManipulationForm = (props: IManipulationProps) => {
  const methods = useForm<IApiKey>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      key: '',
      applicationUrl: '',
      isActive: true,
      application: '',
      userEmail: '',
      supportDetails: { message: '', url: null },
      privileges: [],
      properties: { configit: [], pdm: [] },
      featureFlags: [],
    },
  });
  const error = useSelector((state: AppState) => state.error);
  const dispatch = useDispatch<AppDispatch>();
  const { getValues, control, reset, setValue } = methods;
  const { isValid, isDirty, errors } = methods.formState;
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState('Api');
  const manipulationType = props.type;
  const [displayAlertMessage, setDisplayAlertMessage] = useState<boolean>(false);
  const [applications, setApplications] = useState<IApplication[]>([]);
  const [featureFlags, setFeatureFlags] = useState<IApplicationFeatureFlags[]>([]);
  const [selectedFeatureFlags, setSelectedFeatureFlags] = useState<IApikeyFeatureFlag[]>([]);
  const intialData: IApiKey = {
    name: '',
    description: '',
    key: '',
    applicationUrl: '',
    isActive: true,
    application: '',
    userEmail: '',
    supportDetails: { message: '', url: null },
    privileges: [],
    properties: { configit: [], pdm: [] },
    featureFlags: [],
  };

  const [initialApiKeyData, setInitialApiKeyData] = useState<IApiKey>(intialData);

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, reset, setInitialApiKeyData, dispatch, setSelectedFeatureFlags);
    }
    setApplicationDetails(setApplications, dispatch);
  }, []);
  useEffect(() => {
    setFeatureFlagDetails(setFeatureFlags, dispatch);
  }, [initialApiKeyData]);
  const handleTabChange = (...params: [React.BaseSyntheticEvent, string]) => {
    setTabValue(params[1]);
  };
  const onSubmit: SubmitHandler<IApiKey> = (data) => {
    data.featureFlags = selectedFeatureFlags;
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, data, t, dispatch);
  };
  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };
  useEffect(() => {
    addAlertMessage(error, setDisplayAlertMessage);
  }, [error]);

  return (
    <Box className={'userManipulationTabs ' + (tabValue === 'Features' ? 'overflowY-hidden' : '')}>
      <Collapse in={displayAlertMessage} className="show-alert">
        <Alert className="errorMessage" severity="error" onClose={handleClose}>
          <AlertTitle>
            <ErrorMessage error={error} manipulationType={manipulationType} />
          </AlertTitle>
        </Alert>
      </Collapse>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TabContext value={tabValue || 'Api'}>
            <Box className="dialog-box">
              <TabList onChange={handleTabChange}>
                <Tab
                  data-testid="apikey-deatails-tab"
                  className="dialog-tab text-capitalize"
                  value="Api"
                  label={
                    <span>
                      {t('labels.apikeyDetails')} <span className="asterisk">*</span>
                    </span>
                  }
                />
                <Tab
                  data-testid="apikey-featureflag-tab"
                  className="dialog-tab text-capitalize"
                  value="Features"
                  label={t('labels.feature')}
                />
                <Tab
                  data-testid="apikey-properties-tab"
                  className="dialog-tab text-capitalize"
                  value="Properties"
                  label={t('labels.properties')}
                />
                <Tab
                  data-testid="apikey-support-tab"
                  className="dialog-tab text-capitalize"
                  value="SupportDetails"
                  label={t('labels.supportDetails')}
                />
                <Tab
                  data-testid="apikey-permissions-tab"
                  className="dialog-tab text-capitalize"
                  value="Permissions"
                  label={
                    <span>
                      {t('labels.permissions')} <span className="asterisk">*</span>
                    </span>
                  }
                />
              </TabList>
            </Box>
            <div className={tabValue === 'Api' ? 'd-inline pt-1 show' : 'hide'}>
              <br />
              <APIKeyDetails
                applications={applications}
                t={t}
                control={control}
                getValues={getValues}
                errors={errors}
              />
            </div>
            <div className={tabValue === 'SupportDetails' ? 'd-inline pt-1 show ' : 'hide'}>
              <SupportDetails t={t} control={control} getValues={getValues} errors={errors} />
            </div>
            <div className={tabValue === 'Properties' ? 'd-inline pt-0 show' : 'hide'}>
              <Properties t={t} control={control} getValues={getValues} setValue={setValue} />
            </div>
            <div className={tabValue === 'Features' ? 'featureFlagsTab pt-0 show' : 'hide'}>
              <FeatureFlags
                t={t}
                featureFlags={featureFlags}
                selectedFeatureFlags={selectedFeatureFlags}
                setSelectedFeatureFlags={setSelectedFeatureFlags}
                control={control}
                getValues={getValues}
              />
            </div>
            <div className={tabValue === 'Permissions' ? 'permissionsTab pt-0 show' : 'hide'}>
              {RenderPrivilegesTable({ t, control, getValues, setValue })}
            </div>
          </TabContext>

          <Box className="formActionButtons">
            <Button
              type="submit"
              data-testid="role-button-submit"
              variant="contained"
              size="medium"
              className="text-capitalize"
              disabled={!isValid || !isDirty}
            >
              {manipulationType === EActionType.Create ? t('button.create') : t('button.save')}
            </Button>
            <Button
              size="medium"
              data-testid="role-button-cancel"
              className="text-capitalize"
              onClick={() => {
                methods.reset();
                resetAllErrorInfo();
                setDisplayAlertMessage(false);
              }}
            >
              {t('button.cancel')}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

const APIKeyDetails = ({ applications, t, control, getValues, errors }: IAPIKeyDetailsProp) => {
  const serviceUsersName = AppStore.getState().apiKeys.serviceUsers;
  return (
    <Box className="manipulationForm">
      <Controller
        name="name"
        control={control}
        rules={{
          required: true,
          minLength: stringValidations.name.min,
          pattern: RegExPatterns.AlphaNumeric,
        }}
        render={({ field }) => (
          <TextField
            {...field}
            required
            label={t('labels.name')}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!getValues('isActive')}
            data-testid="apikey-name-input"
          />
        )}
      />

      <br />
      <Collapse in={!!errors.name}>
        <Alert className="errorMessage" severity="error">
          <AlertTitle>{t('messages.nameErrorMessage')} </AlertTitle>
        </Alert>
      </Collapse>
      <br />
      <Controller
        name="applicationUrl"
        control={control}
        rules={{ required: true, pattern: RegExPatterns.Url }}
        render={({ field }) => (
          <TextField
            {...field}
            required
            label={t('labels.applicationUrl')}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!getValues('isActive')}
            data-testid="apikey-applicationUrl-input"
          />
        )}
      />
      <br />
      <Collapse in={!!errors.applicationUrl}>
        <Alert className="errorMessage" severity="error">
          <AlertTitle>{t('messages.urlErrorMessage')} </AlertTitle>
        </Alert>
      </Collapse>
      <br />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('labels.description')}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!getValues('isActive')}
            data-testid="apikey-description-input"
          />
        )}
      />
      <br />
      <fieldset className="inputFieldset w-100 mt-1em">
        <legend className="inputLegend" id="application-name">
          {t('labels.applicationName')} <span className="asterisk">*</span>
        </legend>
        <Controller
          name="application"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup aria-labelledby="applicationName" {...field}>
              <Grid container>
                {applications.map((value) => {
                  return (
                    <Grid key={value.name} className="pt-0" size={3}>
                      <FormControlLabel
                        value={value.name}
                        label={value.name}
                        control={<Radio data-testid="apikey-application-input" />}
                        disabled={!getValues('isActive')}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          )}
        />
      </fieldset>
      <fieldset className="inputFieldset w-100 mt-1em">
        <legend className="inputLegend" id="userEmail">
          {t('labels.userEmail')}
        </legend>
        {serviceUsersName.length < 1 ? (
          <p>{t('messages.noServiceUserAvailable')}</p>
        ) : (
          <Controller
            name="userEmail"
            control={control}
            render={({ field }) => (
              <RadioGroup aria-labelledby="userEmail" {...field}>
                <Grid container>
                  {serviceUsersName.map((value: string) => {
                    return (
                      <Grid key={value} size={8}>
                        <FormControlLabel
                          value={value}
                          label={value}
                          control={<Radio data-testid="apikey-serviceuser-input" />}
                          disabled={!getValues('isActive')}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </RadioGroup>
            )}
          />
        )}
      </fieldset>
      <br />
    </Box>
  );
};

const SupportDetails = ({ t, control, getValues, errors }: ISupportDetailsProp) => {
  return (
    <Box className="supportDetailsForm manipulationForm">
      <Controller
        name="supportDetails.message"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('labels.message')}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!getValues('isActive')}
          />
        )}
      />
      <br />
      <Controller
        name="supportDetails.url"
        control={control}
        rules={{
          pattern: RegExPatterns.Url,
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('labels.url')}
            variant="outlined"
            size="small"
            fullWidth
            className="mt-1em"
            disabled={!getValues('isActive')}
          />
        )}
      />
      <br />
      <Collapse in={!!(errors.supportDetails as FieldErrors<ISupportDetail>)?.url}>
        <Alert className="errorMessage" severity="error">
          <AlertTitle>{t('messages.urlErrorMessage')} </AlertTitle>
        </Alert>
      </Collapse>
      <br />
    </Box>
  );
};

const Properties: React.FC<IManipulationFormBase> = (props) => {
  const { t, control, getValues, setValue } = props;
  const properties = AppStore.getState().apiKeys.properties;
  const formValues = useWatch({ control });
  const createPropertyChangeHandler = useCallback(
    (field: ICheckboxGroupChangeField, origin: string) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        handlePropertyChangeCheck(
          e.target.value,
          e.target.checked,
          origin,
          getValues,
          setValue,
          properties,
          field
        );
      };
    },
    [getValues, setValue, properties]
  );
  return (
    <Box className="propertiesForm">
      {properties &&
        Object.keys(properties).map((origin) => {
          const firstValue = properties[origin][0];
          return (
            <>
              <fieldset className="inputFieldset w-100">
                <legend className="inputLegend">{t(`labels.${origin}`)} </legend>
                {firstValue && (
                  <Grid container>
                    <Grid key={firstValue.code}>
                      <FormControlLabel
                        className="overflowWrap-anywhere"
                        key={firstValue.code}
                        label={firstValue.code + ' (' + firstValue.name + ')'}
                        control={
                          <Checkbox
                            data-testid="apikey-configit-property-checkbox"
                            onChange={(e) =>
                              handlePropertyChangeCheck(
                                e.target.value,
                                e.target.checked,
                                origin,
                                getValues,
                                setValue,
                                properties
                              )
                            }
                            disabled={!getValues('isActive')}
                            checked={(formValues.properties as Record<string, string[]>)[
                              origin
                            ].includes(firstValue.code)}
                            value={firstValue.code}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid container>
                  {properties[origin]?.map((property, i) => {
                    return (
                      i !== 0 &&
                      property.code !== '' && (
                        <Grid key={property.code} size={6}>
                          <FormControlLabel
                            className="overflowWrap-anywhere"
                            key={property.code}
                            label={property.code + ' (' + property.name + ')'}
                            control={
                              <Controller
                                name={`properties.${origin}` as FieldPath<IApiKey>}
                                control={control}
                                render={({ field }) => (
                                  <Checkbox
                                    {...field}
                                    data-testid="apikey-pdm-property-checkbox"
                                    value={property.code}
                                    disabled={
                                      !getValues('isActive') ||
                                      (formValues['properties'] as Record<string, string[]>)[
                                        origin
                                      ].includes('ALL')
                                    }
                                    checked={(formValues.properties as Record<string, string[]>)[
                                      origin
                                    ].includes(property.code)}
                                    onChange={createPropertyChangeHandler(
                                      field as ICheckboxGroupChangeField,
                                      origin
                                    )}
                                  />
                                )}
                              />
                            }
                          />
                        </Grid>
                      )
                    );
                  })}
                </Grid>
              </fieldset>
              <br />
            </>
          );
        })}
    </Box>
  );
};

const handleFeatureFlagChange = (
  checked: boolean,
  flagName: string,
  fieldName: Exclude<keyof IApikeyFeatureFlag, 'name'>,
  selectedFeatureFlags: IApikeyFeatureFlag[],
  setSelectedFeatureFlags: (value: React.SetStateAction<IApikeyFeatureFlag[]>) => void
) => {
  const index = selectedFeatureFlags.findIndex((formDataFlag) => formDataFlag.name === flagName);
  const initialFields = {
    isActive: false,
    editable: false,
    name: '',
    visible: false,
  };
  let updatedFlags: IApikeyFeatureFlag[] = [];
  if (!checked) {
    if (index >= 0) {
      updatedFlags = [...selectedFeatureFlags];
      updatedFlags[index][fieldName] = false;
      if (!Object.values(updatedFlags[index]).includes(true)) {
        //remove the flag if non of the properties/actions are true
        updatedFlags.splice(index, 1);
      }
      setSelectedFeatureFlags(updatedFlags);
    }
  } else if (index >= 0) {
    updatedFlags = [...selectedFeatureFlags];
    updatedFlags[index][fieldName] = true;
    setSelectedFeatureFlags(updatedFlags);
  } else {
    updatedFlags = [...selectedFeatureFlags];
    updatedFlags.push({ ...initialFields, name: flagName, [fieldName]: true });
    setSelectedFeatureFlags(updatedFlags);
  }
};

const FeatureFlags = ({
  t,
  featureFlags,
  selectedFeatureFlags,
  setSelectedFeatureFlags,
  control,
  getValues,
}: IFeatureFlagsProp) => {
  let count = 0;
  // Extract the onChange logic to a separate function
  const handleCheckboxChange = useCallback(
    (
      field: ICheckboxChangeField,
      flagName: string,
      fieldName: Exclude<keyof IApikeyFeatureFlag, 'name'>
    ) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        field.onChange(e.target.checked);
        handleFeatureFlagChange(
          e.target.checked,
          flagName,
          fieldName,
          selectedFeatureFlags,
          setSelectedFeatureFlags
        );
      },
    [selectedFeatureFlags, setSelectedFeatureFlags]
  );
  return (
    <TableContainer component={Paper} className="featureFlagsTable">
      <Table stickyHeader aria-label="featureFlags table">
        <TableHead>
          <TableRow key="featureFlagsHeader">
            <TableCell key="applicationName" style={{ width: '15%' }}>
              {t('labels.applicationName')}
            </TableCell>
            <TableCell key="page" style={{ width: '22.5%' }}>
              {t('labels.page')}
            </TableCell>
            <TableCell key="flagName" style={{ width: '22.5%' }}>
              {t('labels.flagName')}
            </TableCell>
            <TableCell key="isActive" style={{ width: '10%' }}>
              {t('labels.isActive')}
            </TableCell>
            <TableCell key="isVisible" style={{ width: '10%' }}>
              {t('labels.isVisible')}
            </TableCell>
            <TableCell key="isEditable" style={{ width: '10%' }}>
              {t('labels.isEditable')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {featureFlags?.map((application: IApplicationFeatureFlags) => {
            // Render only the Flags that are active and ignore the applications with no active flags
            return (
              application.featureFlags.length > 0 && (
                <>
                  <TableRow key={application.applicationName}>
                    <TableCell
                      rowSpan={application.featureFlags.length + 1}
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {application.applicationName}
                    </TableCell>
                  </TableRow>
                  {application.featureFlags?.map((flag) => {
                    count++;
                    return (
                      flag.isActive && (
                        <TableRow hover key={'' + flag.page + flag.flagName}>
                          <TableCell key="page" sx={{ overflowWrap: 'anywhere' }}>
                            {flag.page}
                          </TableCell>

                          <TableCell key="flagName" sx={{ overflowWrap: 'anywhere' }}>
                            {flag.flagName}
                          </TableCell>

                          <TableCell key="isActive" padding="checkbox" align="center">
                            {flag.isActive ? (
                              <FormControlLabel
                                key={`featureFlags.${flag.flagName}.isActive`}
                                label=""
                                control={
                                  <Controller
                                    name={
                                      `featureFlags.${flag.flagName}.isActive` as FieldPath<IApiKey>
                                    }
                                    control={control}
                                    render={({ field }) => (
                                      <Checkbox
                                        {...field}
                                        disabled={!getValues('isActive')}
                                        id={`featureFlags.${flag.flagName}.isActive`}
                                        onChange={handleCheckboxChange(
                                          field as ICheckboxChangeField,
                                          flag.flagName,
                                          'isActive'
                                        )}
                                        checked={(field.value as boolean) || false}
                                        value={flag.isActive}
                                      />
                                    )}
                                  />
                                }
                                labelPlacement="end"
                                name="isActive"
                              />
                            ) : (
                              <FormControlLabel
                                value="-"
                                control={<></>}
                                name="isActive"
                                label="-"
                                className="cursor-default "
                              />
                            )}
                          </TableCell>

                          <TableCell key="isVisible" padding="checkbox" align="center">
                            {flag.isVisible ? (
                              <FormControlLabel
                                key={`featureFlags.${flag.flagName}.visible`}
                                value={flag.flagName}
                                control={
                                  <Controller
                                    name={
                                      `featureFlags.${flag.flagName}.visible` as FieldPath<IApiKey>
                                    }
                                    control={control}
                                    render={({ field }) => (
                                      <Checkbox
                                        {...field}
                                        disabled={!getValues('isActive')}
                                        id={`featureFlags.${flag.flagName}.visible`}
                                        onChange={handleCheckboxChange(
                                          field as ICheckboxChangeField,
                                          flag.flagName,
                                          'visible'
                                        )}
                                        checked={(field.value as boolean) || false}
                                        value={flag.isVisible}
                                      />
                                    )}
                                  />
                                }
                                labelPlacement="end"
                                name="visible"
                                label=""
                              />
                            ) : (
                              <FormControlLabel
                                value="-"
                                control={<></>}
                                name="visible"
                                label="-"
                                className="cursor-default "
                              />
                            )}
                          </TableCell>

                          <TableCell key="isEditable" padding="checkbox" align="center">
                            {flag.isEditable ? (
                              <FormControlLabel
                                value={flag.flagName}
                                key={`featureFlags.${flag.flagName}.editable`}
                                control={
                                  <Controller
                                    name={
                                      `featureFlags.${flag.flagName}.editable` as FieldPath<IApiKey>
                                    }
                                    control={control}
                                    render={({ field }) => (
                                      <Checkbox
                                        {...field}
                                        disabled={!getValues('isActive')}
                                        id={`featureFlags.${flag.flagName}.editable`}
                                        onChange={handleCheckboxChange(
                                          field as ICheckboxChangeField,
                                          flag.flagName,
                                          'editable'
                                        )}
                                        checked={(field.value as boolean) || false}
                                        value={flag.isEditable}
                                      />
                                    )}
                                  />
                                }
                                labelPlacement="end"
                                name="editable"
                                label=""
                              />
                            ) : (
                              <FormControlLabel
                                value="-"
                                control={<></>}
                                name="editable"
                                label="-"
                                className="cursor-default "
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    );
                  })}
                </>
              )
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RenderPrivilegesTable = (renderPrivilegesTableProps: IManipulationFormBase) => {
  const { t, control, getValues, setValue } = renderPrivilegesTableProps;
  const privileges = AppStore.getState().apiKeys.privileges;

  const privilegesResponse = [...privileges];
  const [initialTab, setInitialTab] = useState('0');
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setInitialTab(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', height: 224 }}>
      <TabContext value={initialTab}>
        <Box>
          <TabList
            orientation="vertical"
            onChange={handleTabChange}
            aria-label="lab API tabs example"
          >
            {privilegesResponse.map((value, index) => (
              <Tab
                key={value.group + ' tab'}
                label={value.group}
                value={index.toString()}
                style={{ paddingLeft: '8px' }}
                className="text-capitalize align-start text-left justify-center permission-tab-panel"
              />
            ))}
          </TabList>
        </Box>
        {privilegesResponse.map((value, index) => (
          <div
            key={value.group}
            className={initialTab === index.toString() ? 'tabpanel-manipulation show' : 'hide'}
          >
            <TableContainer component={Paper} className="api-data-table">
              <Table stickyHeader aria-label="permissions table">
                <TableHead>
                  <TableRow key="permissionsHeader">
                    <TableCell key="resource" style={{ width: '25%', padding: '2%' }}>
                      {t('labels.resource')}
                    </TableCell>
                    <TableCell key="permission" style={{ padding: '2%' }}>
                      {t('labels.permissions')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <RenderPrivilageAsscessAssociations
                    group={value.group}
                    privilegeAccessAssociations={value?.privilegeAccessAssociations}
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </TabContext>
    </Box>
  );
};

/**
 * This function returns the privilege access associations
 * @param {IPrivilegeAccessAssociation[] } privilegeAccessAssociations privilege access associations
 * @param {IApiKey} formData the form data
 * @param {React.SetStateAction<IApiKey>} setFormData set state action
 * @param {IPrivileges} privilegesResponse  privilege access values
 * @returns {React.JSX.Element[]} return the privilege access association check box elements
 */

const RenderPrivilageAsscessAssociations = ({
  group,
  privilegeAccessAssociations,
  control,
  getValues,
  setValue,
}: IRenderPrivilageAsscessAssociationsProp) => {
  const selectedResource = useWatch({
    control,
    name: 'privileges', // field name to watch
  });

  const handleSelectAll = (
    e: React.ChangeEvent<HTMLInputElement>,
    access: IPrivilegeAccessAssociation
  ) => {
    const selected = selectedResource?.filter((res) => res.resource === access.id) ?? [];
    if (e.target.checked) {
      if (selected.length <= 0) {
        setValue(
          'privileges',
          [
            ...(selectedResource ?? []),
            { resource: access.id, accessTypes: access.accessTypes.map((a) => a.id), group: group },
          ],
          { shouldDirty: true, shouldValidate: true }
        );
      } else {
        selected[0].accessTypes = access.accessTypes.map((a) => a.id);
        setValue('privileges', selectedResource, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      setValue(
        'privileges',
        selectedResource?.filter((selected) => selected.resource !== access.id),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  };
  const checkSelected = (access: IPrivilegeAccessAssociation) => {
    return (
      selectedResource?.find((res) => res.resource === access.id)?.accessTypes?.length ===
      access.accessTypes.length
    );
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    itemId: number,
    accessId: number
  ) => {
    const selected = selectedResource?.filter((res) => res.resource === accessId) ?? [];
    if (e.target.checked) {
      if (selected.length <= 0) {
        setValue(
          'privileges',
          [
            ...(selectedResource ?? []),
            { resource: accessId, accessTypes: [itemId], group: group },
          ],
          { shouldDirty: true, shouldValidate: true }
        );
      } else {
        selected[0].accessTypes.push(itemId);
        setValue('privileges', selectedResource, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      const filtered = selected[0]?.accessTypes.filter((acc) => acc !== itemId);
      if (filtered.length == 0) {
        const newSelectedResource = selectedResource?.filter((sel) => sel.resource !== accessId);
        setValue('privileges', newSelectedResource, { shouldDirty: true, shouldValidate: true });
      } else {
        selected[0].accessTypes = filtered;
        setValue('privileges', selectedResource, { shouldDirty: true, shouldValidate: true });
      }
    }
  };

  const checkAccess = (row: IPrivilegeAccessAssociation, id: number): boolean => {
    const rec = selectedResource?.find((p) => p.resource === row['id']);
    let result = false;
    if (rec) {
      result = row['accessTypes'].some(() => rec['accessTypes']?.includes(id));
    }
    return result;
  };

  return privilegeAccessAssociations?.map((access: IPrivilegeAccessAssociation) => (
    <TableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      hover
      tabIndex={-1}
      key={access.resource}
    >
      {/** Resource */}
      <TableCell key="privileges" padding="checkbox" sx={{ paddingLeft: '2%' }}>
        <Controller
          name="privileges"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup {...field}>
              <Grid container>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={access.id.toString()}
                      data-testid="apikey-privilege-resource"
                      checked={checkSelected(access)}
                      disabled={!getValues('isActive')}
                      onChange={(e) => {
                        handleSelectAll(e, access);
                      }}
                    />
                  }
                  label={access.resource}
                />
              </Grid>
            </RadioGroup>
          )}
        />
      </TableCell>
      {/** Permissions */}
      <TableCell key="permissions" padding="checkbox" sx={{ paddingLeft: '2%' }}>
        {access.accessTypes.map((item: IAccessType) => (
          <FormControlLabel
            key={access.resource + item.name}
            value={access['resource']}
            control={
              <Checkbox
                id={access['id'] + '#' + item['id']}
                data-testid="apikey-privilege-permission"
                checked={checkAccess(access, item['id'])}
                disabled={!getValues('isActive')}
                onChange={(e) => {
                  handleChange(e, item.id, access.id);
                }}
              />
            }
            label={item['name']}
            labelPlacement="end"
          />
        ))}
      </TableCell>
    </TableRow>
  ));
};
